import React, { useEffect, useState } from 'react';
import {
  ImageCircle,
  ContextMenu,
  ContextMenuItem,
  ContextMenuDivider,
  IconButton,
  Gutter,
  ContextMenuHeader,
  useBreakpoint,
  useApi,
  CookieUtil,
} from '@axiom/ui';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { isEnvoyUser, isClientUser } from '../../utils/roles-util';
import { WindowUtil } from '../../utils/window-util';
import { EnvUtil } from '../../utils/env-util';
import { LinkUtil } from '../../utils/link-util';
import { NotificationPreferencesModal } from '../NotificationPreferencesModal/NotificationPreferencesModal';
import { AccountLegacyApi } from '../../api/account-legacy-api';
import { UserLegacyApi } from '../../api/user-legacy-api';

export const SharedHeaderMenus = () => {
  const isUserLoggedIn = !!CookieUtil.get('user');
  const [{ data: user } = { data: null }] = useApi(
    isUserLoggedIn ? UserLegacyApi.readUser() : undefined
  );
  const [showPrefModal, setShowPrefModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const modal = searchParams.get('modal');
  const navigate = useNavigate();
  const { accountId } = useParams();
  const { isMobile } = useBreakpoint();

  /**
   * Envoy Users won't have a contact which means they won't have an array of
   * accounts so with this if check, we fill in the structure as if they're
   * not a ClientUser
   */
  const [{ data: accountForNonClient } = { data: null }] = useApi(
    accountId && isEnvoyUser(user)
      ? AccountLegacyApi.getAccount(accountId)
      : undefined
  );

  const account = (() => {
    let acc;

    if (accountForNonClient) {
      return accountForNonClient;
    }

    if (!user?.contact?.accounts?.length) {
      return null;
    }

    if (accountId) {
      acc = user.contact.accounts.find(a => a.id === accountId) || null;
    } else {
      [acc] = user.contact.accounts;
    }

    return acc;
  })();

  const openNotificationPreferences = () => {
    setSearchParams(prev => {
      prev.set('modal', 'notifications');
      return prev;
    });
  };

  const closedNotificationPreferences = () => {
    setSearchParams(prev => {
      prev.delete('modal');
      return prev;
    });
  };

  useEffect(() => {
    setShowPrefModal(!!modal);
  }, [modal]);

  const renderUserLoggedIn = () => {
    const showCompanies = (user?.contact?.accounts?.length ?? 0) >= 2;

    return (
      <ContextMenu
        name="LOGGED_IN_MENU"
        anchor={
          <>
            <Gutter right="12px">
              <ImageCircle imageName={user?.fullName} size="thumbnail" />
            </Gutter>
            <IconButton
              pattern="secondary"
              variation="minimal"
              icon="arrow-down"
            />
          </>
        }
        direction={isMobile ? 'right' : 'left'}
      >
        {showCompanies ? (
          <>
            <ContextMenuHeader>Switch Companies</ContextMenuHeader>
            {(user?.contact?.accounts ?? []).map((a, idx) => {
              return (
                <ContextMenuItem
                  key={a.id}
                  name={
                    a.calculatedName?.toUpperCase().replaceAll(' ', '_') ??
                    `COMPANY_LINK_${idx}`
                  }
                  onClick={() => {
                    navigate(LinkUtil.generateClientProjectsUrl(a));
                  }}
                  selected={a.id === account?.id}
                >
                  {a.calculatedName || a.name}
                </ContextMenuItem>
              );
            })}
            <ContextMenuDivider />
          </>
        ) : null}
        {!!account && isClientUser(user) && (
          <>
            <ContextMenuHeader>My Account</ContextMenuHeader>
            <ContextMenuItem
              onClick={openNotificationPreferences}
              name="NOTIFICATION_PREFERENCES_LINK"
            >
              Notification Preferences
            </ContextMenuItem>
          </>
        )}
        <ContextMenuItem
          onClick={() => {
            window.location.href =
              'mailto:client.answers@axiomlaw.com?subject=Axiom for Client Support Request';
          }}
          name="REPORT_ISSUE_LINK"
        >
          Report Issue
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => {
            window.location.href = `${EnvUtil.loginUrl}/logout`;
          }}
          name="SIGN_OUT_LINK"
        >
          Sign Out
        </ContextMenuItem>
      </ContextMenu>
    );
  };

  const renderUserLoggedOut = () => {
    return (
      <a
        data-test="SIGN_IN"
        href={WindowUtil.getLoginLink()}
        target="_blank"
        rel="noopener noreferrer"
      >
        Sign In
      </a>
    );
  };

  return (
    <>
      <div data-test="SHARED_HEADER">
        {user ? renderUserLoggedIn() : renderUserLoggedOut()}
      </div>
      {showPrefModal ? (
        <NotificationPreferencesModal
          onClose={closedNotificationPreferences}
          user={user}
        />
      ) : null}
    </>
  );
};
